import React, { useEffect, useMemo, useRef, useState } from "react";

// @mui/material
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import Typography from "@mui/material/Typography";
import Drawer from "@mui/material/Drawer";
import Tooltip from "@mui/material/Tooltip";
import Skeleton from "@mui/material/Skeleton";
import { useTheme } from "@mui/material/styles";
import { useBrand } from "../context/BrandContext";

// Permissões
import { useEffectivePermissions, toSet, can } from "../hooks/useEffectivePermissions";
import { useWorkspaceSegment } from "../hooks/useWorkspaceSegment";

// Ícones
import {
  Dashboard,
  PeopleOutline,
  PsychologyOutlined,
  Timeline,
  Inventory2Outlined,
  PeopleAltOutlined,
  SupervisorAccount,
  HubOutlined,
  AccountTreeOutlined,
  BusinessOutlined,
  SettingsSuggestOutlined,
  PaymentOutlined,
  LocalShippingOutlined,
  CategoryOutlined,
  LocalOfferOutlined,
  StairsOutlined,
  CollectionsOutlined,
  ArticleOutlined,
  AllInboxOutlined,
  PolicyOutlined,
  QuestionAnswerOutlined,
  PollOutlined,
  SmartToyOutlined,
  IntegrationInstructionsOutlined,
  Translate,
} from "@mui/icons-material";

// Router
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* =========================== Itens de menu =========================== */

// const menuItems = [
//   { text: "Dashboard", icon: <Dashboard />, link: "/dashboard" },
//   { text: "Atendimento", icon: <AllInboxOutlined />, link: "/chat" },
//   { text: "Pipeline de Vendas", icon: <Timeline />, link: "/sales-pipeline" },
//   { text: "Clientes", icon: <PeopleOutline />, link: "/clients" },
//   { text: "Níveis de consciência", icon: <PsychologyOutlined />, link: "/pipeline" },
//   // { text: "Pesquisas", icon: <PollOutlined />, link: "/surveys" },
//   { text: "Produtos", icon: <Inventory2Outlined />, link: "/products" },
//   // { text: "Campanhas", icon: <CampaignOutlined />, link: "/campaigns" },
// ];

// const settingsItems = [
//   { text: "Empresas", icon: <BusinessOutlined />, link: "/settings/companies" },
//   { text: "Usuários", icon: <PeopleAltOutlined />, link: "/settings/users" },
//   { text: "Cargos", icon: <SupervisorAccount />, link: "/settings/positions" },
//   { text: "Canais", icon: <HubOutlined />, link: "/settings/channels" },
//   { text: "Tipos", icon: <AccountTreeOutlined />, link: "/settings/types" },
//   { text: "Categorias de Produtos", icon: <CategoryOutlined />, link: "/categories" },
//   { text: "Regras de Automação", icon: <SettingsSuggestOutlined />, link: "/settings/automation-rules" },
//   { text: "Formas de Pagamento", icon: <PaymentOutlined />, link: "/settings/payment-methods" },
//   { text: "Métodos de Entrega", icon: <LocalShippingOutlined />, link: "/settings/delivery-methods" },
//   // { text: "Etapas do Pipeline de Vendas", icon: <FlagOutlined />, link: "/pipeline-stages" },
//   { text: "Etapas dos Níveis de Consciência", icon: <StairsOutlined />, link: "/awareness-stages" },
//   { text: "Cupons de Desconto", icon: <LocalOfferOutlined />, link: "/coupons" },
//   { text: "Banners", icon: <CollectionsOutlined />, link: "/banners" },
//   { text: "Conteúdos", icon: <ArticleOutlined />, link: "/contents" },
//   // { text: "Integrações", icon: <IntegrationInstructionsOutlined />, link: "/settings/integrations" },
// ];


const menuItemsDef = [
  { textKey: "nav.dashboard", icon: <Dashboard />, link: "/dashboard", res: "dashboard" },
  { textKey: "nav.chat", icon: <AllInboxOutlined />, link: "/chat", res: "chat" },
  { textKey: "nav.salesPipeline", icon: <Timeline />, link: "/sales-pipeline", res: "sales_pipeline" },
  { textKey: "nav.clients", icon: <PeopleOutline />, link: "/clients", res: "clients" },
  { textKey: "nav.surveys", label: "Pesquisas", icon: <PollOutlined />, link: "/surveys", res: "surveys" },
  { textKey: "nav.awareness", icon: <PsychologyOutlined />, link: "/pipeline", res: "awareness" },
  { textKey: "nav.products", icon: <Inventory2Outlined />, link: "/products", res: "products" },
];

const settingsItemsDef = [
  { textKey: "nav.companies", icon: <BusinessOutlined />, link: "/settings/companies", res: "companies" },
  { textKey: "nav.users", icon: <PeopleAltOutlined />, link: "/settings/users", res: "users" },
  { textKey: "nav.chatbots", icon: <QuestionAnswerOutlined />, link: "/settings/chatbot", res: "chatbot" },
  { textKey: "nav.agents", icon: <SmartToyOutlined />, link: "/agents", res: "agents" },
  { textKey: "nav.positions", icon: <SupervisorAccount />, link: "/settings/positions", res: "positions" },
  { textKey: "nav.channels", icon: <HubOutlined />, link: "/settings/channels", res: "channels" },
  { textKey: "nav.integrations", icon: <IntegrationInstructionsOutlined />, link: "/settings/integrations", res: "integrations" },
  { textKey: "nav.workspaceLanguage", icon: <Translate />, link: "/settings/workspace-language", res: "workspace_i18n" },
  { textKey: "nav.types", icon: <AccountTreeOutlined />, link: "/settings/types", res: "types" },
  { textKey: "nav.categories", icon: <CategoryOutlined />, link: "/categories", res: "categories" },
  { textKey: "nav.automationRules", icon: <SettingsSuggestOutlined />, link: "/settings/automation-rules", res: "automation_rules" },
  { textKey: "nav.antifraudMethods", icon: <PolicyOutlined />, link: "/settings/antifraud-methods", res: "antifraud_methods" },
  { textKey: "nav.paymentMethods", icon: <PaymentOutlined />, link: "/settings/payment-methods", res: "payment_methods" },
  { textKey: "nav.deliveryMethods", icon: <LocalShippingOutlined />, link: "/settings/delivery-methods", res: "delivery_methods" },
  { textKey: "nav.awarenessStages", icon: <StairsOutlined />, link: "/awareness-stages", res: "awareness_stages" },
  { textKey: "nav.coupons", icon: <LocalOfferOutlined />, link: "/coupons", res: "coupons" },
  { textKey: "nav.banners", icon: <CollectionsOutlined />, link: "/banners", res: "banners" },
  { textKey: "nav.contents", icon: <ArticleOutlined />, link: "/contents", res: "contents" },
];

/* =========================== Sidebar =========================== */

const PERM_CACHE_KEY = "perm_scopes_cache";

const Sidebar = ({ collapsed, isMobileOpen, toggleDrawer }) => {
  const brand = useBrand();
  const { t } = useTranslation("app");
  const location = useLocation();
  const { data: permData, isLoading } = useEffectivePermissions();
  const { segment } = useWorkspaceSegment();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const previousPathnameRef = useRef(location.pathname);
  const isCompact = !isMobile && collapsed;

  // Cache de permissões para evitar flash e layout shift
  const [cachedScopes, setCachedScopes] = useState(() => {
    try {
      const raw = sessionStorage.getItem(PERM_CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // Atualiza o cache assim que chegar dado novo
  useEffect(() => {
    if (permData?.scopes && Array.isArray(permData.scopes)) {
      sessionStorage.setItem(PERM_CACHE_KEY, JSON.stringify(permData.scopes));
      setCachedScopes(permData.scopes);
    }
  }, [permData?.scopes]);

  // Fonte de verdade para render:
  // - !isLoading e permData => usa permData
  // - isLoading e tem cache  => usa cache
  // - caso contrário         => null (skeleton)
  const effectiveScopes = useMemo(() => {
    if (!isLoading && permData?.scopes) return permData.scopes;
    if (isLoading && cachedScopes) return cachedScopes;
    return null;
  }, [isLoading, permData?.scopes, cachedScopes]);

  const scopeSet = toSet(effectiveScopes);

  const normalize = (p = "") => p.replace(/\/+$/, "");
  const isSelected = (basePath) => {
    const current = normalize(location.pathname);
    const base = normalize(basePath);
    if (!base || base === "/") return current === "";
    return current === base || current.startsWith(base + "/");
  };

  const canViewAny = (items) => {
    if (!effectiveScopes) return false; // durante loading sem cache: esconda título
    return items.some((item) => can(scopeSet, item.res, "view"));
  };

  const hasVisibleSettings = canViewAny(settingsItemsDef);

  // No mobile, ao trocar de rota, feche o Drawer para evitar "menu travado"
  useEffect(() => {
    if (!isMobile) return;
    const routeChanged = previousPathnameRef.current !== location.pathname;
    if (routeChanged && isMobileOpen) {
      toggleDrawer(false);
    }
    previousPathnameRef.current = location.pathname;
  }, [location.pathname, isMobile, isMobileOpen, toggleDrawer]);

  /* ---------- Render helpers ---------- */

  const SkeletonList = ({ count = 6 }) => (
    <List aria-busy>
      {Array.from({ length: count }).map((_, i) => (
        <ListItem key={`sk-${i}`} sx={{ px: isCompact ? 1 : 2 }}>
          <ListItemIcon sx={{ minWidth: isCompact ? 40 : 56 }}>
            <Skeleton variant="circular" width={24} height={24} />
          </ListItemIcon>
          {!isCompact && <Skeleton variant="text" width="60%" />}
        </ListItem>
      ))}
    </List>
  );

  const NavList = ({ items }) => {
    // Sem scopes efetivos ainda → skeletons
    if (!effectiveScopes) {
      return <SkeletonList count={Math.min(items.length, 8)} />;
    }

    // Filtra estritamente pelo que o usuário pode ver e pelo segmento do workspace (quando definido)
    const visible = items.filter((item) => {
      if (!can(scopeSet, item.res, "view")) return false;
      if (Array.isArray(item.segments) && item.segments.length > 0) {
        return item.segments.includes(segment);
      }
      return true;
    });

    if (visible.length === 0) return null;

    const primaryForItem = (item) => {
      if (item.res === "billing") {
        return t("nav.billingWorkspace");
      }
      return item.label || t(item.textKey);
    };

    return (
      <List>
        {visible.map((item, index) => {
          const active = isSelected(item.link);
          const label = primaryForItem(item);
          return (
            <ListItem
              key={index}
              component={RouterLink}
              to={item.link}
              selected={active}
              onClick={() => {
                if (isMobile) toggleDrawer(false);
              }}
              sx={{
                color: active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
                backgroundColor: active ? theme.palette.primary.main : "transparent",
                "&:hover": {
                  backgroundColor: active ? theme.palette.primary.main : theme.palette.action.hover,
                },
              }}
            >
              <Tooltip title={isCompact ? label : ""} placement="right">
                <ListItemIcon
                  sx={{
                    color: active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
                    minWidth: isCompact ? 40 : 56,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
              </Tooltip>
              {!isCompact && <ListItemText primary={label} />}
            </ListItem>
          );
        })}
      </List>
    );
  };

  const drawerContent = (
    <Box
      sx={{
        width: isMobile ? "200px" : collapsed ? "60px" : "200px",
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        display: "flex",
        flexDirection: "column",
        transition: "width 0.3s",
      }}
    >
      <Box className="tour-sidebar-nav" sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {/* Menu principal */}
        <Box className="tour-sidebar-main">
          <NavList items={menuItemsDef} />
        </Box>

        {/* Só mostra Divider + Título se houver pelo menos 1 item visível de configurações */}
        {hasVisibleSettings && (
          <Box className="tour-sidebar-settings">
            <Divider />

            {!isCompact && (
              <Typography
                variant="body2"
                sx={{ padding: "16px", fontWeight: "bold", color: theme.palette.text.secondary }}
              >
                {t("nav.settingsHeading")}
              </Typography>
            )}

            <NavList items={settingsItemsDef} />
          </Box>
        )}
      </Box>

      {!isCompact && (
        <Box sx={{ padding: "16px", textAlign: "left", fontSize: "12px", color: theme.palette.text.disabled }}>
          © {new Date().getFullYear()} {brand.name}
        </Box>
      )}
    </Box>
  );

  return isMobile ? (
    <Drawer
      variant="temporary"
      open={isMobileOpen}
      onClose={() => toggleDrawer(false)}
      ModalProps={{ keepMounted: true }}
      sx={{
        display: { xs: "block", md: "none" },
        "& .MuiDrawer-paper": { boxSizing: "border-box", width: "200px", marginTop: "50px" },
      }}
    >
      {drawerContent}
    </Drawer>
  ) : (
    drawerContent
  );
};

export default Sidebar;
